/**
 * Iter XX.98 — Parent-audience detector.
 *
 * A tiny sibling of `paidInstagramGate.js`. Persists whether the
 * current browser arrived on a link tagged `?audience=parent` (or
 * any UTM variant we standardise for the parent Meta campaign) so
 * that the flag-gated parent-first onboarding continues to show
 * across page reloads / return visits WITHIN THE SAME BROWSER,
 * even after the query string has been stripped.
 *
 * Contract:
 *   · Detects `?audience=parent` (case-insensitive) on load and
 *     persists to localStorage under `g_parent_audience_v1`.
 *   · `isParentAudience()` returns true when either the current
 *     URL params OR the persisted flag say so. Falls back to
 *     `false` on any storage failure — never throws.
 *   · Signed-in visitors are still eligible; the eventual UI gate
 *     itself continues to skip when a token is present (existing
 *     `shouldShowParentEntryGate` handles that).
 *
 * This module is deliberately narrow. It DOES NOT read UTMs beyond
 * `audience=parent`, DOES NOT touch analytics (the callers do that
 * with the existing Clarity pipeline), and DOES NOT decide whether
 * to *render* the gate — that is the caller's responsibility using
 * both this signal AND the `parent_first_onboarding_enabled` feature
 * flag.
 */
const KEY = "g_parent_audience_v1";

/** Persist the parent audience flag. Idempotent, never overwrites
 *  with a falsy value once set — the parent-first cohort is
 *  browser-scoped for the duration of the campaign. */
export function markParentAudience() {
  try {
    localStorage.setItem(KEY, JSON.stringify({
      audience:    "parent",
      captured_at: new Date().toISOString(),
    }));
  } catch { /* storage disabled — non-fatal */ }
}

/** Snapshot check without side effects. Used inside render passes. */
export function isParentAudience() {
  // 1. URL param wins (may be pre-persist on first frame).
  try {
    const u = new URL(window.location.href);
    const v = (u.searchParams.get("audience") || "").toLowerCase();
    if (v === "parent") return true;
  } catch { /* noop */ }
  // 2. Persisted marker.
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && parsed.audience === "parent";
  } catch { return false; }
}

/** Consume URL params once and persist. Call from InstantChatLanding
 *  on mount; safe to call multiple times. */
export function captureParentAudienceFromUrl() {
  try {
    const u = new URL(window.location.href);
    const v = (u.searchParams.get("audience") || "").toLowerCase();
    if (v === "parent") {
      markParentAudience();
      return true;
    }
  } catch { /* noop */ }
  return false;
}
